import { ecId, record, type LogRow, type Row } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { inventoryRowList, readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { fromBaseUuid } from '../compile/from-base-context';
import { phpJsonTruthy } from '../compile/php-json-values';
import type { RollSource } from '../actions/roll-source';
import { statusRows } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { emptySpawnPlan, type SpawnRowPlan, type SpawnReaders } from '../spawn/row-plan';

const COINS = new Set(['DDF5AA5E-BCA8-D5A7-ED03-E9A82356A65E', 'B0B6D994-03C5-D49A-C1FF-49C3C1723A4E',
    '0AB2A68A-8FDC-AAC3-5E79-D9BA6395A551', 'F52A7C08-B1B3-F1AF-81D5-1919DD4691CA', 'C42F9276-0791-46E0-985A-22B3117096DE']);

/** Compiled carriers retain attached picture rows at the encounter, owner, or inventory node. */
export function merchantDeathPictures(target: Row, encounter: Row): Row[] {
    const pictures: Row[] = [], seen = new Set<Row>();
    const collect = (row: Row): void => {
        if (seen.has(row)) return;
        seen.add(row);
        pictures.push(...inventoryRowList(row.pictures).filter(picture => fromBaseUuid(readItemField(picture, 'picture_id'))
            && fromBaseUuid(readItemField(picture, 'picture_attached_id'))));
        const owner = readInventoryField(row, 'inventory_id');
        if (fromBaseUuid(owner)) for (const picture of inventoryRowList(record(row.pictures)?.inventory_picture))
            pictures.push({ ...picture, picture_attached_id: readItemField(picture, 'picture_attached_id') ?? owner });
        for (const child of [...readInventoryRows(row, 'inventory'), ...readInventoryRows(row, 'inventory_in_container')]) collect(child);
    };
    collect(target); collect(encounter);
    return pictures;
}

/** handle_merchant_death_inventory: coins and worn gear remain; d4+1 shuffled loose rows remain.
 * The caller supplies the current carried tree and attached pictures, after any in-hand drops. */
export function planMerchantDrops(target: Row, pictures: readonly Row[], read: Pick<SpawnReaders, 'value'>,
    roll: RollSource): { plan: SpawnRowPlan; rows: LogRow[] } {
    const plan = emptySpawnPlan(), rows: LogRow[] = [];
    if (!phpJsonTruthy(read.value(target, 'merchant'))) return { plan, rows };
    const inventory = readInventoryRows(target, 'inventory');
    // PHP checks that the inventory layer exists before rolling even when its array is empty.
    if (inventory.length === 0) return { plan, rows };
    const eligible = inventory.filter(row => !phpJsonTruthy(readInventoryField(row, 'equipped'))
        && !COINS.has(String(readItemField(row, 'item_id') ?? '')));
    const keep = roll.d(4) + 1;
    for (let i = eligible.length - 1; i > 0; i--) {
        const j = roll.d(i + 1) - 1;
        [eligible[i], eligible[j]] = [eligible[j]!, eligible[i]!];
    }
    const seen = new Set<string>(), removedStatuses = new Set<string>(), removedPictures = new Set<string>();
    const retire = (row: Row, child: boolean): void => {
        const id = String(readInventoryField(row, 'inventory_id') ?? '');
        if (!fromBaseUuid(id)) throw new Error('death:merchant-inventory-identity-not-held');
        if (seen.has(id)) return;
        seen.add(id);
        plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: id, inventory_deleted: 1 },
            before: { possess_id: String(readInventoryField(row, 'possess_id') ?? ''), inventory_deleted: Number(readInventoryField(row, 'inventory_deleted') ?? 0) } });
        for (const picture of pictures) if (readItemField(picture, 'picture_attached_id') === id) {
            const pictureId = readItemField(picture, 'picture_id');
            if (!fromBaseUuid(pictureId)) throw new Error('death:merchant-picture-identity-not-held');
            if (removedPictures.has(pictureId)) continue;
            removedPictures.add(pictureId);
            plan.writes.push({ table: 'death_inventory_pictures', op: 'update', row: { picture_id: pictureId, picture_deleted: 1 },
                before: { picture_attached_id: id, picture_deleted: Number(readItemField(picture, 'picture_deleted') ?? 0) } });
        }
        for (const nested of readInventoryRows(row, 'inventory_in_container')) retire(nested, true);
        // The native recursive child helper clears statuses on descendants; its root merchant loop does not.
        if (child) for (const status of statusRows(row, 'target')) {
            const statusId = String(statusScalar(status, 'status_id') ?? '');
            if (!fromBaseUuid(statusId)) throw new Error('death:merchant-status-identity-not-held');
            if (removedStatuses.has(statusId)) continue;
            removedStatuses.add(statusId);
            plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: statusId, status_deleted: 1 },
                before: { status_deleted: Number(statusScalar(status, 'status_deleted') ?? 0), status_target_id: String(statusScalar(status, 'status_target_id') ?? id) } });
        }
    };
    for (const row of eligible.slice(keep)) retire(row, false);
    if (plan.writes.length) rows.push({ roll_type_1: 'merchant_inventory_teleported', roll_type_2: null,
        roll_item_id: null, roll_by: ecId(target), roll_to: null, roll_result: 0 });
    return { plan, rows };
}
